import { useState, useEffect, useCallback } from 'react';
import { wizardsApi } from '../services/wizardsApi';

// Simple in-memory cache to prevent refetching during the same session
const wizardCache = {};

/**
 * Fetches the current state of a wizard by key.
 * Used by page tours to check whether the tour has already been completed.
 * 
 * @param {string} wizardKey - e.g. 'sina_pwa_dashboard_tour_completed'
 */
export const useWizardQuery = (wizardKey) => {
    const [data, setData] = useState(wizardCache[wizardKey] || null);
    const [isLoading, setIsLoading] = useState(!wizardCache[wizardKey]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (wizardCache[wizardKey]) {
            setData(wizardCache[wizardKey]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        wizardsApi.getWizard(wizardKey)
            .then((resData) => {
                wizardCache[wizardKey] = resData;
                setData(resData);
            })
            .catch((err) => {
                // Return default state if wizard doesn't exist yet (404)
                const defaultState = { wizard: wizardKey, show_wizard: true, current_step: 1, completed: false };
                wizardCache[wizardKey] = defaultState;
                setData(defaultState);
                setError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [wizardKey]);

    return { data, isLoading, error };
};

/**
 * Mutation to mark a wizard as completed.
 * Used by page tours when the user finishes the Joyride tour.
 * 
 * @param {string} wizardKey - e.g. 'sina_pwa_dashboard_tour_completed'
 */
export const useCompleteWizardMutation = (wizardKey) => {
    const [isLoading, setIsLoading] = useState(false);
    
    const mutate = useCallback(async (variables, options = {}) => {
        setIsLoading(true);
        try {
            const result = await wizardsApi.complete(wizardKey);
            // Optimistically update cache
            if (wizardCache[wizardKey]) {
                wizardCache[wizardKey] = { ...wizardCache[wizardKey], completed: true, show_wizard: false };
            } else {
                wizardCache[wizardKey] = { wizard: wizardKey, show_wizard: false, current_step: 1, completed: true };
            }
            if (options.onSuccess) options.onSuccess(result);
        } catch (error) {
            if (options.onError) options.onError(error);
        } finally {
            setIsLoading(false);
        }
    }, [wizardKey]);

    return { mutate, isLoading };
};
